import { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Typography, Divider } from '@mui/material';
import { SortOrder } from 'further-types/utils';

import PageContainer from 'components/PageContainer';
import { ADVISER } from 'adminConstants/routes';
import { useGetRole } from 'hooks/ui/useGetRole';
import FeeAmendment from './FeeAmendment';
import AddressInput from './AddressInput';
import BasicDetails from './BasicDetails';
import BankDetailsInput from './BankDetailsInput';
import {
  useAdviser,
  useUpdateAdviserFeeAmendments,
} from 'hooks/data/adviser/useAdviser';
import api from 'lib/trpcClient';
import {
  AdviserEditLevel,
  getAdviserEditLevel,
} from 'utils/adviserPermissions';
import PageContent from 'components/PageContent';
import ButtonGroup from 'components/ButtonGroup';
import Button from 'components/Button';
import RecordView from 'components/RecordView';
import TextField from 'components/FormElements/AppTextInput';
import Spacing from 'components/Spacing';
import Row from 'components/Layout/Row';

export type ErrorText = {
  errorValue: Record<string, string>;
  addressError: Array<Record<string, string>>;
  changeToInitialInvestmentFeesErrors: Array<Record<string, string>>;
  commissionOnInvestmentFeesErrors: Array<Record<string, string>>;
};

const AdviserForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const { isSuperAdmin, firmId } = useGetRole();
  const adviser = useAdviser({ params: { id } });
  const updateFeeAmendments = useUpdateAdviserFeeAmendments();
  const parentOrganisations = api.organisation.list.useQuery({
    isChild: true,
    firmId,
    order: SortOrder.Asc,
    orderBy: 'name',
  });

  const emptyChangeToInitialInvestmentFees = {
    firmId: isSuperAdmin ? '' : firmId,
    amendByPercentage: 0,
  };
  const emptyCommissionOnInitialInvestmentFees = {
    firmId: isSuperAdmin ? '' : firmId,
    amendByPercentage: 0,
    vatPercentage: 0,
  };
  const emptyAddress = {
    address1: '',
    address2: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
  };

  const [adviserData, setAdviserData] = useState({
    organisationData: {
      name: null,
      organisationId: null,
    },
    firstName: '',
    lastName: '',
    middleName: '',
    email: '',
    phoneNumber: '',
    fcaNumber: '',
    address: [emptyAddress],
    firmId: isSuperAdmin ? null : firmId,
    initialFeeHasChanges: false,
    changeToInitialInvestmentFees: [emptyChangeToInitialInvestmentFees],
    commissionFeeHasChanges: false,
    commissionOnInitialInvestmentFees: [emptyCommissionOnInitialInvestmentFees],
    associatedFirms: [],
    hasMoreThanOneFirm: false,
    hasMoreThanOneLinkedFirm: false,
    bankAccountName: '',
    bankAccountNumber: '',
    bankName: '',
    bankSortCode: '',
    iban: '',
    swift: '',
  });
  const [editLevel, setEditLevel] = useState<AdviserEditLevel>(
    AdviserEditLevel.None,
  );
  const [errorText, setErrorText] = useState<ErrorText>({
    errorValue: {},
    addressError: [],
    changeToInitialInvestmentFeesErrors: [],
    commissionOnInvestmentFeesErrors: [],
  });

  const breadcrumbs = [
    { label: 'Dashboard' },
    { label: 'Adviser', link: ADVISER },
    { label: id ? 'Update Adviser' : 'Add Adviser', link: '/', isActive: true },
  ];

  const validate = () => {
    const errors: Record<string, string> = {};
    const addressError: Array<Record<string, string>> = [];
    const changeToInitialInvestmentFeesErrors: Array<Record<string, string>> =
      [];
    const commissionOnInvestmentFeesErrors: Array<Record<string, string>> = [];

    if (!id && !adviserData?.firmId) {
      errors.firmId = 'Please select firm.';
    }

    if (!adviserData?.email) {
      errors.email = 'Please enter email address.';
    }

    if (
      adviserData?.fcaNumber &&
      !new RegExp(/^[a-zA-Z0-9]+$/i).test(adviserData?.fcaNumber)
    ) {
      errors.fcaNumber = 'An FCA number can only contain letters or numbers.';
    }

    if (adviserData?.initialFeeHasChanges) {
      adviserData?.changeToInitialInvestmentFees.forEach((item, index) => {
        //@ts-expect-error
        if (!item.amendByPercentage || item.amendByPercentage === '0') {
          changeToInitialInvestmentFeesErrors[index] = {
            amendByPercentage: 'Please enter net fee change.',
          };
        }

        if (!item.firmId) {
          changeToInitialInvestmentFeesErrors[index] = {
            ...changeToInitialInvestmentFeesErrors[index],
            firmId: 'Please select a firm.',
          };
        }
      });
    }
    if (adviserData?.commissionFeeHasChanges) {
      adviserData?.commissionOnInitialInvestmentFees.forEach((item, index) => {
        //@ts-expect-error
        if (!item.amendByPercentage || item.amendByPercentage === '0') {
          commissionOnInvestmentFeesErrors[index] = {
            amendByPercentage: 'Please enter commission.',
          };
        }

        if (!item.firmId) {
          commissionOnInvestmentFeesErrors[index] = {
            ...commissionOnInvestmentFeesErrors[index],
            firmId: 'Please select a firm.',
          };
        }

        if (!item.vatPercentage && item.vatPercentage !== 0) {
          commissionOnInvestmentFeesErrors[index] = {
            ...commissionOnInvestmentFeesErrors[index],
            vatPercentage: 'Please enter the VAT rate for the commission.',
          };
        }
      });
    }

    setErrorText({
      errorValue: errors,
      addressError,
      changeToInitialInvestmentFeesErrors,
      commissionOnInvestmentFeesErrors,
    });

    return {
      errors,
      addressError,
      isError: !!(
        Object.keys(errors).length ||
        addressError.length ||
        changeToInitialInvestmentFeesErrors.length ||
        commissionOnInvestmentFeesErrors.length
      ),
    };
  };

  const onSubmit = async () => {
    const validateData = validate();

    if (!validateData.isError) {
      const {
        organisationData,
        initialFeeHasChanges,
        changeToInitialInvestmentFees,
        commissionFeeHasChanges,
        commissionOnInitialInvestmentFees,
        ...rest
      } = adviserData;

      if (!isSuperAdmin && id) {
        await updateFeeAmendments.mutateAsync({
          id,
          changeToInitialInvestmentFees: initialFeeHasChanges
            ? changeToInitialInvestmentFees
            : [],
          commissionOnInitialInvestmentFees: commissionFeeHasChanges
            ? commissionOnInitialInvestmentFees
            : [],
        });
      }

      const value: Record<string, any> = { ...rest };

      if (organisationData?.organisationId) {
        value.organisationId = organisationData?.organisationId;
      } else {
        value.organisationId = null;
      }
      value.changeToInitialInvestmentFees = initialFeeHasChanges
        ? changeToInitialInvestmentFees
        : [];
      value.commissionOnInitialInvestmentFees = commissionFeeHasChanges
        ? commissionOnInitialInvestmentFees
        : [];

      if (!id) {
        adviser.create.mutate(value);
      } else if (editLevel !== AdviserEditLevel.None) {
        value.id = id;
        //@ts-expect-error
        adviser.update.mutate(value);
      }
    }
  };

  useEffect(() => {
    if (adviser.create.isSuccess) {
      history.push(ADVISER);
    }
  }, [adviser.create.isSuccess]);

  useEffect(() => {
    if (adviser.fetch.data?.adviser && parentOrganisations.isFetched) {
      const {
        adviser: {
          firstName,
          lastName,
          middleName,
          email,
          changeToInitialInvestmentFees = [],
          commissionOnInitialInvestmentFees = [],
          organisationId,
          fcaNumber,
          phoneNumber,
          address,
          associatedFirms,
          hasMoreThanOneLinkedFirm,
          bankAccountName,
          bankAccountNumber,
          bankName,
          bankSortCode,
          allInvestmentsWithSingleFirm,
          iban,
          swift,
          internationalBankCode,
        },
      } = adviser.fetch.data;

      const findExistingOrganisation =
        parentOrganisations.data?.organisation?.filter(
          (org) => organisationId === org?._id,
        );

      const safeAddress = address?.length ? address : [emptyAddress];
      const initialFeeHasChanges = changeToInitialInvestmentFees.length > 0;
      const commissionFeeHasChanges =
        commissionOnInitialInvestmentFees?.length > 0;

      const data = {
        firstName,
        lastName,
        middleName,
        fcaNumber,
        email,
        address: safeAddress,
        initialFeeHasChanges,
        commissionFeeHasChanges,
        changeToInitialInvestmentFees: initialFeeHasChanges
          ? changeToInitialInvestmentFees
          : [emptyChangeToInitialInvestmentFees],
        commissionOnInitialInvestmentFees: commissionFeeHasChanges
          ? commissionOnInitialInvestmentFees
          : [emptyCommissionOnInitialInvestmentFees],
        organisationData: {
          name: findExistingOrganisation?.[0]?.name,
          organisationId: findExistingOrganisation?.[0]?._id,
        },
        phoneNumber,
        associatedFirms: associatedFirms?.map((firm) => firm._id),
        hasMoreThanOneLinkedFirm,
        bankAccountName,
        bankAccountNumber,
        bankName,
        bankSortCode,
        allInvestmentsWithSingleFirm,
        iban,
        swift,
        internationalBankCode,
      };

      //@ts-expect-error
      setAdviserData(data);
      setEditLevel(
        getAdviserEditLevel(isSuperAdmin, hasMoreThanOneLinkedFirm, firmId),
      );
    }
  }, [adviser.fetch.data, parentOrganisations.isFetched]);

  return (
    <PageContainer
      loading={!!id && adviser.fetch.isLoading}
      heading={id ? 'Advisers: Update adviser' : 'Advisers: Create adviser'}
      breadcrumbs={breadcrumbs}
    >
      <PageContent>
        <RecordView>
          {!id && (
            <>
              <Spacing size="sm">
                <Typography variant="body2">
                  To add an adviser, either quick invite them with their email
                  address or enter their full details below.
                </Typography>
                <Row spacing="sm">
                  <TextField
                    placeholder="Quick invite by email address"
                    value={adviserData.email}
                    onChange={(e) =>
                      setAdviserData({ ...adviserData, email: e.target.value })
                    }
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onSubmit}
                    loading={adviser.create.isPending}
                    whiteSpace="nowrap"
                  >
                    Quick invite
                  </Button>
                </Row>
              </Spacing>
              <Divider />
            </>
          )}

          <BasicDetails
            isBeingEdited={!!id}
            setAdviserData={setAdviserData}
            adviserData={adviserData}
            parentOrganisations={parentOrganisations.data?.organisation ?? []}
            errorText={errorText}
            editLevel={editLevel}
          />

          {adviserData?.address?.map((singleAddress, i) => (
            <AddressInput
              key={i}
              address={singleAddress}
              index={i}
              isBeingEdited={id}
              setAdviserData={setAdviserData}
              adviserData={adviserData}
              errorText={errorText}
              canEdit={editLevel !== AdviserEditLevel.None}
            />
          ))}

          <BankDetailsInput
            editDisabled={!!id && editLevel !== AdviserEditLevel.All}
            adviserData={adviserData}
            setAdviserData={setAdviserData}
            errorText={errorText}
            hasMoreThanOneLinkedFirm={adviserData?.hasMoreThanOneLinkedFirm}
          />

          <FeeAmendment
            adviserData={adviserData}
            setAdviserData={setAdviserData}
            errors={errorText}
          />

          <ButtonGroup>
            <Link to={ADVISER}>
              <Button variant="outlined">Cancel</Button>
            </Link>
            <Button
              variant="contained"
              color="primary"
              loading={
                adviser.create.isPending ||
                adviser.update.isPending ||
                updateFeeAmendments.isPending
              }
              onClick={onSubmit}
            >
              {id ? 'Update' : 'Invite adviser'}
            </Button>
          </ButtonGroup>
        </RecordView>
      </PageContent>
    </PageContainer>
  );
};
export default AdviserForm;
